import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ColorOptions, HorizontalAlignmentOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent, NICmsContentField } from '@icms/core/content';
import { TButtonStyles, buttonStylesOptions } from '../buttons/button-style/button-style.component';

export namespace NTextComponent {
    export interface ITextContent {
        overline?: string;
        overlineColor?: string;
        title?: string;
        titleColor?: string;
        titleClass?: string;
        paragraph?: string;
        paragraphColor?: string;
        align?: 'left' | 'center ' | 'right';
        buttons?: IButton[];
        matchButtonsHeight?: boolean;
    }

    export interface IButton {
        copy: string;
        url: string;
        style?: TButtonStyles;
    }

    export const getFields = (boxTextAlign = false, showColorOptions = false, isTitleRequired = true) => {
        const fields: NICmsContentField.IField<any, any>[] = [
            {
                key: 'overline',
                type: NICmsContentField.EFieldType.text,
                title: 'Overline',
                default: 'Lorem ipsum dolor sit amet'
            },
            <NICmsContentField.IChoiceField>{
                key: 'overlineColor',
                type: NICmsContentField.EFieldType.choice,
                previewMode: 'color',
                title: 'Overline color',
                options: ColorOptions,
                canShow: () => showColorOptions
            },
            {
                key: 'title',
                type: NICmsContentField.EFieldType.textarea,
                title: 'Title',
                default: 'Long heading title on two lines lorem ipsum',
                required: isTitleRequired
            },
            <NICmsContentField.IChoiceField>{
                key: 'titleColor',
                type: NICmsContentField.EFieldType.choice,
                previewMode: 'color',
                title: 'Title color',
                options: ColorOptions,
                canShow: () => showColorOptions
            },
            <NICmsContentField.IChoiceField>{
                key: 'titleClass',
                type: NICmsContentField.EFieldType.choice,
                title: 'Title class',
                options: TextStyleOptions
            },
            {
                key: 'paragraph',
                type: NICmsContentField.EFieldType.textarea,
                title: 'Paragraph',
                default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.<br> Aenean pellentesque commodo pulvinar. '
            },
            <NICmsContentField.IChoiceField>{
                key: 'paragraphColor',
                type: NICmsContentField.EFieldType.choice,
                previewMode: 'color',
                title: 'Paragraph color',
                options: ColorOptions,
                canShow: () => showColorOptions
            },
            <NICmsContentField.IRepeaterField>{
                key: 'buttons',
                type: NICmsContentField.EFieldType.repeater,
                title: 'Buttons',
                itemNameField: 'copy',
                itemName: 'Button',
                maxSize: 4,
                default: [],
                fields: [
                    {
                        key: 'copy',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Copy',
                        default: 'Button label',
                        required: true
                    },
                    {
                        key: 'url',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Url',
                        default: '/',
                        required: true
                    },
                    {
                        key: 'style',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Style',
                        options: buttonStylesOptions,
                        default: 'button'
                    }
                ]
            },
            {
                key: 'matchButtonsHeight',
                type: NICmsContentField.EFieldType.toggle,
                title: 'Match buttons height in one row'
            }
        ];

        if (boxTextAlign) {
            fields.push(<NICmsContentField.IChoiceField>{
                key: 'align',
                type: NICmsContentField.EFieldType.choice,
                title: 'Align',
                options: HorizontalAlignmentOptions,
                default: 'left'
            });
        }

        return fields;
    };
}

@Component({
    selector: 'text',
    templateUrl: './text.html',
    styleUrls: ['./text.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextComponent extends UiComponent {
    @Input()
    variant?: 'default' | 'light' = 'default';

    @Input()
    align?: 'left' | 'center' = 'left';

    @Input({ required: true })
    @ICmsContent({
        watchDepth: 2
    })
    content!: NTextComponent.ITextContent;

    protected get contentAlign(): string {
        return this.content.align || this.align || 'left';
    }
}
